// Render engine: WebGPURenderer (falls back to WebGL 2 on its own), resize with a DPR cap, the
// frame loop, quality tiers, and the post-processing pipeline scaffold later modules extend.
//
//   scene pass (MRT: output [+ emissive] [+ normal], depth)
//      → hdr effects in `order` (GTAO, bloom, DoF, grade…)
//      → tone map + sRGB output (renderOutput)
//      → ldr effects in `order` (FXAA/SMAA need sRGB input)
//      → screen
//
// Effects register with engine.registerPost({...}); the chain rebuilds on change or tier switch.

import * as THREE from 'three/webgpu';
import { emissive, mrt, normalView, output, packNormalToRGB, pass, renderOutput } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import type PassNode from 'three/src/nodes/display/PassNode.js';

export type QualityTier = 'low' | 'med' | 'high';
export type Backend = 'webgpu' | 'webgl2';

export interface TierSettings {
  /** Device pixel ratio cap. */
  dprCap: number;
  shadows: 'on' | 'off';
  shadowMapSize: number;
  /** MSAA samples of the scene pass (0 = off; AA modules may replace it). */
  samples: number;
}

export const TIERS: Readonly<Record<QualityTier, TierSettings>> = {
  low: { dprCap: 1, shadows: 'off', shadowMapSize: 1024, samples: 0 },
  // med: SMAA (post.ts) instead of MSAA: four samples of a three-target MRT pass cost more memory
  // and bandwidth than the whole post chain.
  med: { dprCap: 1.5, shadows: 'on', shadowMapSize: 2048, samples: 0 },
  high: { dprCap: 2, shadows: 'on', shadowMapSize: 4096, samples: 4 },
};

/** Extra scene-pass outputs an effect can ask for. */
export type MrtChannel = 'emissive' | 'normal';

export interface PostContext {
  renderer: THREE.WebGPURenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  tier: QualityTier;
  /** The scene pass (depth: scenePass.getTextureNode('depth'), viewZ, linear depth…). */
  scenePass: PassNode;
  /** A requested MRT channel as a texture node. */
  channel(name: MrtChannel): Node<'vec4'>;
}

export interface PostEffect {
  name: string;
  /** Ascending within its stage. */
  order: number;
  /** 'hdr' runs before tone mapping, 'ldr' after the sRGB output transform. */
  stage: 'hdr' | 'ldr';
  /** Tiers that run this effect (default: all). */
  tiers?: QualityTier[];
  /** MRT channels this effect reads. */
  mrt?: MrtChannel[];
  build(input: Node<'vec4'>, ctx: PostContext): Node<'vec4'>;
}

export interface EngineOptions {
  backend?: 'auto' | 'webgl2';
  tier?: QualityTier;
}

export interface FrameStats {
  fps: number;
  frameMs: number;
  drawCalls: number;
  triangles: number;
}

type FrameHook = (dt: number, time: number) => void;

const FPS_SMOOTHING = 0.1;
const MAX_DT = 0.1;
const TONE_EXPOSURE = 1.0;

/**
 * The terrain fragment shader samples one texture per data layer (hex data, borders, fog, lens,
 * painted arrays...), and a WebGPU device is created with 16 per stage unless it asks for more.
 * Ask for what the adapter has, up to this many.
 */
const SAMPLED_TEXTURES_WANTED = 32;

interface GpuLimits {
  maxSampledTexturesPerShaderStage: number;
}

/** Limits to request: more sampled textures per stage where the adapter offers them, else the defaults. */
async function requiredLimits(backend: Backend): Promise<Partial<GpuLimits>> {
  if (backend === 'webgl2') {
    return {};
  }
  const gpu = (navigator as { gpu?: { requestAdapter(o?: object): Promise<{ limits: GpuLimits } | null> } }).gpu;
  const adapter = await gpu?.requestAdapter({ powerPreference: 'high-performance', featureLevel: 'compatibility' });
  const offered = adapter?.limits.maxSampledTexturesPerShaderStage ?? 0;
  return offered > 0 ? { maxSampledTexturesPerShaderStage: Math.min(offered, SAMPLED_TEXTURES_WANTED) } : {};
}

export class Engine {
  readonly renderer: THREE.WebGPURenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly backend: Backend;
  readonly container: HTMLElement;
  tier: QualityTier;
  /** Frames rendered so far. */
  frames = 0;
  /** Seconds since start. */
  time = 0;

  private readonly pipeline: THREE.RenderPipeline;
  private scenePass: PassNode | null = null;
  private effects: PostEffect[] = [];
  private readonly hooks = new Set<FrameHook>();
  private readonly afterHooks = new Set<FrameHook>();
  private readonly resizeObserver: ResizeObserver;
  private lastMs = 0;
  private fps = 0;
  private frameMs = 0;
  private info = { calls: 0, triangles: 0 };
  private dirty = true;
  private looping = false;

  private constructor(container: HTMLElement, renderer: THREE.WebGPURenderer, camera: THREE.PerspectiveCamera, tier: QualityTier) {
    this.container = container;
    this.renderer = renderer;
    this.camera = camera;
    this.tier = tier;
    const backend = renderer.backend as unknown as { isWebGPUBackend?: boolean };
    this.backend = backend.isWebGPUBackend === true ? 'webgpu' : 'webgl2';
    this.pipeline = new THREE.RenderPipeline(renderer);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
  }

  /** Create the renderer inside `container` and wait for the backend. */
  static async create(container: HTMLElement, camera: THREE.PerspectiveCamera, opts: EngineOptions = {}): Promise<Engine> {
    const backend: Backend = opts.backend === 'webgl2' ? 'webgl2' : 'webgpu';
    const renderer = new THREE.WebGPURenderer({
      antialias: false,
      forceWebGL: backend === 'webgl2',
      powerPreference: 'high-performance',
      requiredLimits: await requiredLimits(backend),
    });
    await renderer.init();
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = TONE_EXPOSURE;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.info.autoReset = false;
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.touchAction = 'none';
    container.appendChild(renderer.domElement);

    const engine = new Engine(container, renderer, camera, opts.tier ?? 'med');
    engine.resize();
    return engine;
  }

  /** Add a post effect; returns a function that removes it. The same name replaces. */
  registerPost(effect: PostEffect): () => void {
    this.effects = this.effects.filter((e) => e.name !== effect.name);
    this.effects.push(effect);
    this.dirty = true;
    return () => {
      this.effects = this.effects.filter((e) => e.name !== effect.name);
      this.dirty = true;
    };
  }

  /** Run `hook(dt, time)` before each frame renders; returns an unsubscribe. */
  onFrame(hook: FrameHook): () => void {
    this.hooks.add(hook);
    return () => this.hooks.delete(hook);
  }

  /** Run `hook(dt, time)` after each frame renders. */
  afterFrame(hook: FrameHook): () => void {
    this.afterHooks.add(hook);
    return () => this.afterHooks.delete(hook);
  }

  setTier(tier: QualityTier): void {
    if (tier === this.tier) {
      return;
    }
    this.tier = tier;
    this.dirty = true;
    this.resize();
  }

  settings(): TierSettings {
    return TIERS[this.tier];
  }

  start(): void {
    this.lastMs = performance.now();
    this.looping = true;
    void this.renderer.setAnimationLoop((ms) => this.frame(ms));
  }

  stop(): void {
    this.looping = false;
    void this.renderer.setAnimationLoop(null);
  }

  /**
   * Render one frame now (used by the harness to force a frame when the loop is throttled). With
   * `dt` the clocks advance by exactly that many seconds, whatever the wall clock says: the
   * director mode's fixed step (the loop is stopped, so nothing else advances them).
   */
  renderOnce(dt?: number): void {
    if (dt === undefined) {
      this.frame(performance.now());
      return;
    }
    this.advance(dt);
  }

  stats(): FrameStats {
    return { fps: this.fps, frameMs: this.frameMs, drawCalls: this.info.calls, triangles: this.info.triangles };
  }

  resize(): void {
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, TIERS[this.tier].dprCap));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Compile the scene's pipelines without blocking, for the render target and outputs the scene
   * pass really uses (renderer.compileAsync on the bare scene builds them for the canvas instead,
   * and those pipelines then fail inside the post chain). `extra` runs inside the same pause, for
   * compiles that switch the render target too (globe.ts warm).
   */
  async compileAsync(extra?: () => Promise<void>): Promise<void> {
    if (this.dirty) {
      this.rebuild();
    }

    // The pass switches the renderer's target and outputs across the await; a frame drawn in
    // between would build the output quad's pipeline for the wrong target. Pause the loop.
    const wasRunning = this.looping;
    this.stop();
    try {
      await this.scenePass?.compileAsync(this.renderer);
      await extra?.();

      // renderer.compileAsync files its render objects under another render context than render()
      // does, so the first real frame would rebuild every model's shader in JS (~8 ms each; ~250 ms
      // with a city in view). One real draw here makes them in the context the frames use.
      this.pipeline.render();
    } finally {
      if (wasRunning) {
        this.start();
      }
    }
  }

  dispose(): void {
    this.stop();
    this.resizeObserver.disconnect();
    this.scenePass?.dispose();
    this.pipeline.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private frame(ms: number): void {
    const dt = Math.min(MAX_DT, Math.max(0, (ms - this.lastMs) / 1000));
    this.lastMs = ms;
    this.advance(dt);
  }

  /** Advance the clocks by `dt` seconds, run the frame hooks and render. */
  private advance(dt: number): void {
    this.time += dt;
    if (dt > 0) {
      this.frameMs = dt * 1000;
      const inst = 1 / dt;
      this.fps = this.fps === 0 ? inst : this.fps + (inst - this.fps) * FPS_SMOOTHING;
    }

    for (const hook of this.hooks) {
      hook(dt, this.time);
    }
    if (this.dirty) {
      this.rebuild();
    }
    this.renderer.info.reset();
    this.pipeline.render();
    // `render.calls` counts since start; `drawCalls` is this frame (reset above).
    this.info = { calls: this.renderer.info.render.drawCalls, triangles: this.renderer.info.render.triangles };
    this.frames++;
    for (const hook of this.afterHooks) {
      hook(dt, this.time);
    }
  }

  /** Rebuild the post chain from the registered effects for the current tier. */
  private rebuild(): void {
    this.dirty = false;
    const tier = this.tier;
    const active = this.effects.filter((e) => !e.tiers || e.tiers.includes(tier)).sort((a, b) => a.order - b.order);
    const channels = new Set<MrtChannel>(active.flatMap((e) => e.mrt ?? []));

    this.scenePass?.dispose();
    const scenePass = pass(this.scene, this.camera, { samples: TIERS[tier].samples });
    const outputs: Record<string, Node> = { output };
    if (channels.has('emissive')) {
      outputs.emissive = emissive;
    }
    if (channels.has('normal')) {
      outputs.normal = packNormalToRGB(normalView);
    }
    if (channels.size > 0) {
      scenePass.setMRT(mrt(outputs));
    }
    this.scenePass = scenePass;

    const ctx: PostContext = {
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      tier,
      scenePass,
      channel: (name) => scenePass.getTextureNode(name) as unknown as Node<'vec4'>,
    };

    let node = scenePass.getTextureNode('output') as unknown as Node<'vec4'>;
    for (const e of active.filter((x) => x.stage === 'hdr')) {
      node = e.build(node, ctx);
    }
    const ldr = active.filter((x) => x.stage === 'ldr');
    if (ldr.length > 0) {
      this.pipeline.outputColorTransform = false;
      node = renderOutput(node) as unknown as Node<'vec4'>;
      for (const e of ldr) {
        node = e.build(node, ctx);
      }
    } else {
      this.pipeline.outputColorTransform = true;
    }
    this.pipeline.outputNode = node;
    this.pipeline.needsUpdate = true;
  }
}
